import userProfilePicture from 'assets/images/users/user-dummy-img.jpg';
import { genders } from 'constants/campaign';
import { useDispatch } from 'react-redux';
import { toggleSocialAccountView } from 'slices/ui/reducer';
import { AppDispatch } from 'store';
import { DiscoveredCreator } from 'types/creator-discovery';
import { formatNumber } from 'utils/helpers';

interface DiscoveredCreatorCardProps {
    creator: DiscoveredCreator;
    isSelected?: boolean;
    onToggleSelect?: (creatorId: string) => void;
}

const getPlatformUrl = (creator: DiscoveredCreator) => {
    if (creator.platform === 'ig') return `https://instagram.com/${creator.handle}`;
    if (creator.platform === 'yt') return `https://youtube.com/@${creator.handle}`;
    return null;
};

const DiscoveredCreatorCard = ({
    creator,
    isSelected = false,
    onToggleSelect,
}: DiscoveredCreatorCardProps) => {
    const platformUrl = getPlatformUrl(creator);
    const gender = creator.ig?.gender;
    const ageBucket = creator.ig?.age_bucket;
    const engagementScore = creator.engagement_score ?? 'NA';
    const dispatch: AppDispatch = useDispatch();

    return (
        <article
            className={`discovered-creator-row${isSelected ? ' discovered-creator-row--selected' : ''}`}
            onClick={() => {
                dispatch(toggleSocialAccountView({
                    socialAccountId: creator.uuid,
                    influencerId: creator.mobile ?? undefined,
                }));
            }}
        >
            <div
                className="discovered-creator-row__select"
                onClick={(e) => {
                    e.stopPropagation();
                    onToggleSelect?.(creator.uuid);
                }}
            >
                <input
                    type="checkbox"
                    className="form-check-input discovered-creator-row__checkbox"
                    checked={isSelected}
                    onChange={() => onToggleSelect?.(creator.uuid)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select ${creator.name}`}
                />
            </div>
            <div className="discovered-creator-row__profile">
                <div className="discovered-creator-row__avatar-wrap">
                    <img
                        src={creator.profile_picture_url?.split('=')[0] ?? userProfilePicture}
                        referrerPolicy="no-referrer"
                        className="discovered-creator-row__avatar"
                        alt={creator.name}
                    />
                    {creator.platform === 'ig' && (
                        <span className="discovered-creator-row__platform-badge">
                            <i className="ri-instagram-line" />
                        </span>
                    )}
                    {creator.platform === 'yt' && (
                        <span className="discovered-creator-row__platform-badge discovered-creator-row__platform-badge--yt">
                            <i className="ri-youtube-fill" />
                        </span>
                    )}
                </div>
                <div className="discovered-creator-row__info">
                    <div className="discovered-creator-row__name-row">
                        <span className="discovered-creator-row__name">{creator.name}</span>
                        {(gender || ageBucket) && (
                            <div className="discovered-creator-row__tags">
                                {gender && (
                                    <span className="discovered-creator-row__tag">
                                        {genders[gender as keyof typeof genders] ?? gender}
                                    </span>
                                )}
                                {ageBucket && <span className="discovered-creator-row__tag">{ageBucket}</span>}
                            </div>
                        )}
                    </div>
                    <span className="discovered-creator-row__handle">@{creator.handle}</span>
                </div>
            </div>

            <div className="discovered-creator-row__metrics">
                <div className="discovered-creator-row__metric">
                    <span className="discovered-creator-row__metric-label">Engg Score</span>
                    <span className="discovered-creator-row__metric-value">{engagementScore}</span>
                </div>
                <div className="discovered-creator-row__metric">
                    <span className="discovered-creator-row__metric-label">Avg Views</span>
                    <span className="discovered-creator-row__metric-value">
                        {creator.average_views ? formatNumber(creator.average_views) : 'NA'}
                    </span>
                </div>
                <div className="discovered-creator-row__metric">
                    <span className="discovered-creator-row__metric-label">Followers</span>
                    <span className="discovered-creator-row__metric-value">
                        {formatNumber(creator.followers_count)}
                    </span>
                </div>
            </div>

            <div className="discovered-creator-row__actions">
                <a
                    href={platformUrl ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                    className="discovered-creator-row__action-btn"
                    aria-label={`Open ${creator.name} profile`}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!platformUrl) e.preventDefault();
                    }}
                >
                    <i className="mdi mdi-arrow-top-right" />
                </a>
            </div>
        </article>
    );
};

export default DiscoveredCreatorCard;
